import { Component, Host, h, Prop, Element, Watch, Method, Event, EventEmitter, State } from '@stencil/core';
import { InputChangeEventDetail, InputType } from './input-interface';
import { Attributes, hasSlot, inheritAriaAttributes, observeAriaAttributes } from '../../utils';
import { getFormFieldId, connectStopPropagateFormEvents, disconnectStopPropagateFormEvents } from '../../utils/form.util';
/**
 * @slot label - The label's text.
 * @slot system-icon - System icon at the end of input for calendar, date, select, etc. Only for internal use.
 *
 * @part input-group - The component's input-group element which wraps input and label.
 * @part input - The component's input element.
 * @part label - The component's label element.
 * @part suffix - The component's suffix element for icons.
 * @part suffix-clear-icon - The component's suffix-clear-icon element for icons.
 */
@Component({
  tag: 'o2-input',
  styleUrl: 'input.scss',
  scoped: true,
})
export class O2Input {
  private input!: HTMLInputElement;
  private inputId = `o2-input-${inputIds++}`;

  private passwordIcon?: HTMLO2IconElement;

  private hasLabelSlot: boolean = false;
  private hasLabelSlotWithoutName: boolean = false;
  private hasSystemIconSlot: boolean = false;
  private hasClearIconSlot: boolean = false;

  private cleanupListeners: (event: Event) => void;

  /**
   * Instance of MutationObserver
   */
  private mutationObserver: MutationObserver;

  /**
   * Inherited attributes from the host element.
   */
  @State() inheritedAttributes: Attributes = {};

  @Element() private el!: HTMLO2InputElement;

  /**
   * Indicates whether the input allows propagation of native events.
   */
  @Prop() native?: boolean = false;

  /**
   * Indicates error
   */
  @Prop({
    reflect: true,
  })
  error?: boolean = false;

  /**
   * Indicates warning
   */
  @Prop({
    reflect: true,
  })
  warning?: boolean = false;

  /**
   * Indicates whether and how the text value should be automatically capitalized as it is entered/edited by the user.
   */
  @Prop() autocapitalize? = 'off';

  /**
   * Indicates whether the value of the control can be automatically completed by the browser.
   */
  @Prop() autocomplete?: 'on' | 'off' | 'username' | 'current-password' | 'new-password' | 'one-time-code' = 'off';

  /**
   * Whether auto correction should be enabled when the user is entering/editing the text value.
   */
  @Prop() autocorrect?: 'on' | 'off' = 'off';

  /**
   * This Boolean attribute lets you specify that a form control should have input focus when the page loads.
   */
  @Prop() autofocus? = false;

  /**
   * If `true`, the user cannot interact with the input.
   */
  @Prop({
    reflect: true,
  })
  disabled? = false;

  /**
   * A hint to the browser for which keyboard to display.
   * Possible values: `"none"`, `"text"`, `"tel"`, `"url"`,
   * `"email"`, `"numeric"`, `"decimal"`, and `"search"`.
   */
  @Prop() inputmode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';

  /**
   * The minimum value, which must not be greater than its maximum (max attribute) value.
   */
  @Prop() min?: string;

  /**
   * If the value of the type attribute is `text`, `email`, `search`, `password`, `tel`, or `url`, this attribute specifies the minimum number of characters that the user can enter.
   */
  @Prop() minlength?: number;

  /**
   * The maximum value, which must not be less than its minimum (min attribute) value.
   */
  @Prop() max?: string;

  /**
   * If the value of the type attribute is `text`, `email`, `search`, `password`, `tel`, or `url`, this attribute specifies the maximum number of characters that the user can enter.
   */
  @Prop() maxlength?: number;

  /**
   * The name of the control, which is submitted with the form data.
   */
  @Prop() name?: string;

  /**
   * Instructional text that shows before the input has a value.
   */
  @Prop() placeholder?: string;

  /**
   * If `true`, the user cannot modify the value.
   */
  @Prop({
    reflect: true,
  })
  readonly? = false;

  /**
   * If `true`, the user must fill in a value before submitting a form.
   */
  @Prop() required? = false;

  /**
   * If `true`, the requirement text is shown. It depends on required property. If required is true, requirement text will be "(povinné)", otherwise it displays "(nepovinné)"
   */
  @Prop() showRequirementText? = false;

  /**
   * Works with the min and max attributes to limit the increments at which a value can be set.
   * Possible values are: `"any"` or a positive floating point number.
   */
  @Prop() step?: string;

  /**
   * The type of control to display. The default type is text.
   */
  @Prop() type?: InputType = 'text';

  /**
   * The value of the input.
   */
  @Prop({ mutable: true }) value?: string;

  /**
   * The label text of the input.
   */
  @Prop() label?: string;

  /**
   * A regular expression that the value is checked against.
   */
  @Prop() pattern?: string;

  /**
   * Icon name to show before input text.
   * @todo generate list of icons and as a type. e.g. type IconName = IconNameSecondary | IconNameSolid
   */
  @Prop() leadingIcon?: string;

  /**
   * Update the native input element when the value changes
   */
  @Watch('value')
  protected valueChanged() {
    this.el.querySelector('input').value = this.getValue();
  }

  /**
   * Update state when disabled attribute has changed.
   */
  @Watch('disabled')
  protected disabledChanged(value: boolean) {
    const input = this.el.querySelector('input');

    input.disabled = value;
  }

  /**
   * Emitted when a keyboard input occurred.
   */
  @Event() o2Input!: EventEmitter<InputEvent>;

  /**
   * Emitted when keyboard input on keyup with delay
   */
  @Event() o2Keyup!: EventEmitter<InputEvent>;

  /**
   * Emitted when the value has changed.
   */
  @Event() o2Change!: EventEmitter<InputChangeEventDetail>;

  /**
   * Emitted when the input loses focus.
   */
  @Event() o2Blur!: EventEmitter<FocusEvent>;

  /**
   * Emitted when the input has focus.
   */
  @Event() o2Focus!: EventEmitter<FocusEvent>;

  /**
   * Emitted when the input is ready.
   */
  @Event() o2Ready!: EventEmitter<Event>;

  /**
   * Sets focus on the native `input` in `o2-input`. Use this method instead of the global
   * `input.focus()`.
   */
  @Method()
  async setFocus() {
    if (this.input) {
      this.input.focus();
    }
  }

  /**
   * Sets blur on the native `input` in `o2-input`. Use this method instead of the global
   * `input.blur()`.
   * @internal
   */
  @Method()
  async setBlur() {
    if (this.input) {
      this.input.blur();
    }
  }

  private getValue(): string {
    return this.value;
  }

  private onChange = (event: InputEvent) => {
    this.value = (event.target as HTMLInputElement).value;

    this.o2Change.emit({ value: this.value == null ? this.value : this.value.toString() });
  };

  private onInput = (ev: InputEvent) => {
    const input = ev.target as HTMLInputElement | null;
    if (input) {
      this.value = input.value || '';
    }
    this.o2Input.emit(ev as InputEvent);
  };

  private onKeyup = (ev: Event) => {
    this.o2Keyup.emit(ev as InputEvent);
  };

  private onBlur = () => {
    this.o2Blur.emit();
  };

  private onFocus = () => {
    this.o2Focus.emit();
  };

  private focusInputOnClick = () => {
    this.input.focus();
  };

  private togglePassword = () => {
    const type = this.input.getAttribute('type');

    this.input.setAttribute('type', type === 'password' ? 'text' : 'password');
    this.passwordIcon.setAttribute('name', type === 'password' ? 'hide' : 'view');
  };

  connectedCallback() {
    // check if there is any elements as slots (children of Host element).
    this.hasLabelSlot = hasSlot(this.el as HTMLElement, '[slot="label"]');
    this.hasSystemIconSlot = hasSlot(this.el as HTMLElement, '[slot="system-icon"]');
    this.hasClearIconSlot = hasSlot(this.el as HTMLElement, '[slot="clear-icon"]');

    this.mutationObserver = observeAriaAttributes(this.el as HTMLElement, () => {
      // merge attributes
      const newAttributes = inheritAriaAttributes(this.el as HTMLElement);
      this.inheritedAttributes = { ...this.inheritedAttributes, ...newAttributes };
    });
  }

  componentWillLoad() {
    this.inheritedAttributes = inheritAriaAttributes(this.el as HTMLElement);
  }

  componentDidRender() {
    this.o2Ready.emit();
  }

  componentDidLoad() {
    const inputElement = this.el.querySelector('input');

    if (!this.native) {
      this.cleanupListeners = connectStopPropagateFormEvents(inputElement);
    }
  }

  disconnectedCallback() {
    // Remove event listeners
    if (!this.native && this.cleanupListeners) {
      const inputElement = this.el.querySelector('input');
      disconnectStopPropagateFormEvents(inputElement, this.cleanupListeners);
    }
    this.mutationObserver?.disconnect();
  }

  render() {
    const hasLabel = !!this.label || this.hasLabelSlot;

    const isTypePassword = this.type === 'password';

    const hasSuffix = this.hasClearIconSlot || this.hasSystemIconSlot || isTypePassword;
    const hasDoubleSuffix = this.hasClearIconSlot && (this.hasSystemIconSlot || isTypePassword);

    return (
      <Host>
        <div class={`o2-input-group ${this.disabled ? 'o2-input-group--disabled' : ''} ${this.readonly ? 'o2-input-group--readonly' : ''}`} part="input-group">
          {hasLabel && (
            <label
              class={`o2-label ${this.error ? 'o2-label--error' : ''} ${this.warning ? 'o2-label--warning' : ''}`}
              htmlFor={this.inputId}
              part="label"
              id={`${this.inputId}-label`}
            >
              <span class="o2-label__text" title={this.label}>
                {this.label && this.label}
                {this.hasLabelSlot && <slot name="label"></slot>}
                {this.hasLabelSlotWithoutName && <slot></slot>}
              </span>

              {this.showRequirementText && <span class="o2-label__requirement"> {this.required ? 'Povinné' : 'Nepovinné'}</span>}
            </label>
          )}

          <div class="o2-input-container">
            {this.leadingIcon && (
              <div class="prefix" onClick={this.focusInputOnClick}>
                <o2-icon name={this.leadingIcon} size={20} color="gray-medium" aria-hidden="true"></o2-icon>
              </div>
            )}

            <input
              ref={el => (this.input = el)}
              id={this.inputId}
              class={`o2-input ${this.error ? 'o2-input--error' : ''} ${this.warning ? 'o2-input--warning' : ''} ${this.leadingIcon ? 'o2-input--has-prefix' : ''}
              ${hasSuffix ? 'o2-input--has-suffix' : ''} ${hasDoubleSuffix ? 'o2-input--has-double-suffix' : ''}`}
              value={this.value}
              name={this.name}
              required={this.required}
              disabled={this.disabled}
              aria-labelledby={`${this.inputId}-label`}
              aria-describedby={getFormFieldId(this.el) ? `${getFormFieldId(this.el)}-help` : null}
              autoCapitalize={this.autocapitalize}
              autoComplete={this.autocomplete}
              autoCorrect={this.autocorrect}
              autoFocus={this.autofocus}
              inputMode={this.inputmode}
              min={this.min}
              max={this.max}
              minLength={this.minlength}
              maxLength={this.maxlength}
              pattern={this.pattern}
              placeholder={this.placeholder || ''}
              readonly={this.readonly}
              step={this.step}
              type={this.type}
              onInput={this.onInput}
              onBlur={this.onBlur}
              onFocus={this.onFocus}
              onChange={this.onChange}
              onKeyUp={this.onKeyup}
              part="input"
              {...this.inheritedAttributes}
            />

            {hasSuffix && (
              <div class="suffix" part="suffix">
                {this.hasClearIconSlot && (
                  <div class="suffix__icon" part="suffix-clear-icon">
                    <slot name="clear-icon" />
                  </div>
                )}

                {isTypePassword && (
                  <div class="suffix__icon">
                    <o2-icon ref={el => (this.passwordIcon = el)} name="view" color="blue-action" size={28} safeArea={4} onClick={this.togglePassword}></o2-icon>
                  </div>
                )}

                {this.hasSystemIconSlot && (
                  <div class="suffix__icon">
                    <slot name="system-icon" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Host>
    );
  }
}

let inputIds = 0;
